import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';

import {
  BenchmarkCoverageDiagnosticItem,
  BenchmarkFillKind,
  BenchmarkFillPreview,
  HISTORICAL_SCENARIO,
  applyBenchmarkFill,
  fetchBenchmarkFillPreview,
  selectors,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import { formatters } from 'services';

// Gap (days between the missing date and the nearest real source price) thresholds for the
// fill staleness warning: small = likely a holiday/hiccup, large = probably a different regime.
const GAP_OK_DAYS = 4;
const GAP_WARN_DAYS = 14;
const gapColor = (gap: number) =>
  gap <= GAP_OK_DAYS ? 'green' : gap <= GAP_WARN_DAYS ? 'orange' : 'red';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  sessionId: number;
};

const formatCoverageDate = (value: string | null) => {
  if (!value) {
    return '-';
  }
  return formatters.dateOnly(value);
};

const formatCoverageTimestamp = (value: string | null) => {
  if (!value) {
    return 'Not computed yet';
  }

  return value.replace('T', ' ').replace(/\.\d+/, '').replace('+00:00', ' UTC');
};

const renderDateTags = (dates: string[], color: string, emptyLabel: string) => {
  if (dates.length === 0) {
    return <Text type="secondary">{emptyLabel}</Text>;
  }

  return (
    <div style={{ maxHeight: 160, overflowY: 'auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {dates.map(date => (
          <Tag color={color} key={date} style={{ marginRight: 0 }}>
            {formatCoverageDate(date)}
          </Tag>
        ))}
      </div>
    </div>
  );
};

const BenchmarkCoverageDiagnosticsDrawer: React.FC<Props> = ({
  open,
  onClose,
  sessionId,
}) => {
  const [activeTab, setActiveTab] = useState('index_funds');
  const [search, setSearch] = useState('');
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const session = useSelector(selectors.sessionById(sessionId));
  const diagnostics = session?.benchmark_coverage_diagnostics;
  const waiting = session?.benchmark_coverage_diagnostics_waiting;
  // Real-time: coverage is over the session's ELAPSED dates and admin-only (nothing published), so
  // the snapshot labels re-scope away from the historical "published / required" wording.
  const isHistorical = session?.scenario_type === HISTORICAL_SCENARIO;

  const items =
    activeTab === 'index_funds'
      ? diagnostics?.index_funds || []
      : diagnostics?.equities || [];

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return items;
    }

    return items.filter(item =>
      [
        item.ric,
        item.name,
        item.issuer_name,
        item.asset_class,
        item.geography,
        item.currently_eligible ? 'eligible' : 'missing',
        item.available_to_trainer ? 'trainer' : 'hidden',
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [items, search]);

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: 'RIC',
        dataIndex: 'ric',
        key: 'ric',
        width: 140,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
      // Classification metadata lives on index funds only; the equities tab omits these columns.
      ...(activeTab === 'index_funds'
        ? [
            {
              title: 'Asset class',
              dataIndex: 'asset_class',
              key: 'asset_class',
              width: 130,
              ellipsis: true,
              render: (value?: string) => value || '—',
            },
            {
              title: 'Geography',
              dataIndex: 'geography',
              key: 'geography',
              width: 130,
              ellipsis: true,
              render: (value?: string) => value || '—',
            },
          ]
        : []),
      {
        title: 'Status',
        key: 'status',
        width: 220,
        render: (_: unknown, record: BenchmarkCoverageDiagnosticItem) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Tag color={record.currently_eligible ? 'green' : 'red'}>
              {record.currently_eligible ? 'Eligible live' : 'Missing dates'}
            </Tag>
            <Tag color={record.available_to_trainer ? 'blue' : 'default'}>
              {record.available_to_trainer
                ? 'Visible to trainer'
                : 'Not in trainer'}
            </Tag>
            {record.needs_recompute && (
              <Tag color="orange">Needs recompute</Tag>
            )}
          </div>
        ),
      },
      {
        title: 'Missing count',
        dataIndex: 'missing_count',
        key: 'missing_count',
        width: 120,
      },
      {
        title: 'First missing date',
        dataIndex: 'first_missing_date',
        key: 'first_missing_date',
        width: 150,
        render: (value: string | null) => formatCoverageDate(value),
      },
      {
        title: 'Inspect',
        key: 'inspect',
        width: 110,
        render: (_: unknown, record: BenchmarkCoverageDiagnosticItem) => {
          const expanded = expandedRowKeys.includes(record.id);
          return (
            <Button
              size="small"
              onClick={() => setExpandedRowKeys(expanded ? [] : [record.id])}
            >
              {expanded ? 'Hide' : 'Inspect'}
            </Button>
          );
        },
      },
    ],
    [expandedRowKeys, activeTab]
  );

  const diagnosticsSummary = diagnostics?.summary;

  // --- Admin "fill missing date" (index funds + equities) ---
  const dispatch = useDispatch();
  // IndexFund and Equity have separate id spaces, so everything below is keyed by `kind`.
  const kind: BenchmarkFillKind =
    activeTab === 'index_funds' ? 'index_fund' : 'equity';
  const fillWaiting = session?.benchmark_fill_waiting;
  const previews = session?.benchmark_fill_previews || {};

  // Multiple trays can be open at once (the default expand icons aren't accordion), so preview
  // the carry-forward source for EVERY expanded row's oldest missing date -- not just the first,
  // or the others get stuck on "Checking source…".
  const expandedTargets = items
    .filter(item => expandedRowKeys.includes(item.id) && item.first_missing_date)
    .map(item => ({ id: item.id, date: item.first_missing_date as string }));
  // Stable dep: re-run when the set of (row, oldest-missing-date) changes -- which also covers a
  // fill advancing a row's oldest missing date.
  const expandedTargetsKey = expandedTargets
    .map(target => `${target.id}:${target.date}`)
    .join('|');

  useEffect(() => {
    expandedTargets.forEach(({ id, date }) => {
      if (previews[`${kind}:${id}:${date}`]) {
        return;
      }
      dispatch(
        fetchBenchmarkFillPreview({ session_id: sessionId, kind, id, date })
      );
    });
    // Depend on expandedTargetsKey (the id:date set) + kind; the guard reads the latest preview
    // cache. Not depending on `previews`/`expandedTargets` avoids re-dispatch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, kind, expandedTargetsKey]);

  const handleFill = (assetId: number, date: string) => {
    dispatch(applyBenchmarkFill({ session_id: sessionId, kind, id: assetId, date }));
  };

  const renderFillAvailable = (
    record: BenchmarkCoverageDiagnosticItem,
    target: string,
    preview: BenchmarkFillPreview
  ) => {
    const gap = preview.gap_days ?? 0;
    const large = gap > GAP_WARN_DAYS;
    const sourceClose =
      preview.source_close != null
        ? formatters.priceFormat(preview.source_close)
        : '-';

    return (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space wrap>
          <Text>
            Carry forward {sourceClose} from{' '}
            {formatCoverageDate(preview.source_date)}
          </Text>
          <Tag color={gapColor(gap)}>
            fill price from {gap} day{gap === 1 ? '' : 's'} before missing date
          </Tag>
          {preview.source_was_synthetic && (
            <Tag color="purple">source is a prior fill</Tag>
          )}
        </Space>
        {large ? (
          <Popconfirm
            title={`This source is ${gap} days old — likely a different regime. Fill anyway?`}
            okText="Fill anyway"
            cancelText="Cancel"
            onConfirm={() => handleFill(record.id, target)}
          >
            <Button type="primary" danger size="small" loading={fillWaiting}>
              Fill this date
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="primary"
            size="small"
            loading={fillWaiting}
            onClick={() => handleFill(record.id, target)}
          >
            Fill this date
          </Button>
        )}
      </Space>
    );
  };

  const renderFillSection = (record: BenchmarkCoverageDiagnosticItem) => {
    const target = record.first_missing_date;
    if (!target) {
      return null;
    }
    const preview = previews[`${kind}:${record.id}:${target}`];

    return (
      <div style={{ background: '#fafafa', padding: 12, borderRadius: 4 }}>
        <Text strong>
          Fill oldest missing date — {formatCoverageDate(target)}
        </Text>
        <div style={{ marginTop: 8 }}>
          {!preview ? (
            <Space>
              <Spin size="small" />
              <Text type="secondary">Checking source…</Text>
            </Space>
          ) : !preview.has_source ? (
            <Alert
              type="warning"
              showIcon
              message={
                kind === 'equity'
                  ? 'No prior complete bar (close, bid, ask) to carry forward for this equity.'
                  : 'No prior non-zero price to carry forward for this fund.'
              }
            />
          ) : (
            renderFillAvailable(record, target, preview)
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title="Benchmark Coverage Diagnostics"
      placement="right"
      width={1000}
      onClose={onClose}
      open={open}
      destroyOnHidden
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="This view checks current history coverage. Trainers only receive changes after benchmark coverage is recomputed."
        />

        <Descriptions
          bordered
          size="small"
          column={1}
          title="Session coverage snapshot"
        >
          <Descriptions.Item
            label={isHistorical ? 'Published funds' : 'Funds fully covered'}
          >
            {diagnosticsSummary
              ? `${diagnosticsSummary.eligible_index_funds_count} / ${diagnosticsSummary.total_index_funds_count}`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={isHistorical ? 'Published equities' : 'Equities fully covered'}
          >
            {diagnosticsSummary
              ? `${diagnosticsSummary.eligible_equities_count} / ${diagnosticsSummary.total_equities_count}`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={isHistorical ? 'Required dates' : 'Elapsed dates'}>
            {diagnosticsSummary?.required_dates_count ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Last recomputed">
            {isHistorical
              ? formatCoverageTimestamp(diagnosticsSummary?.last_computed_at || null)
              : 'Computed live'}
          </Descriptions.Item>
        </Descriptions>

        <Collapse bordered={false} style={{ background: 'transparent' }}>
          <Collapse.Panel
            key="required-session-dates"
            header={
              <Text strong>
                {isHistorical ? 'Required session dates' : 'Elapsed session dates'} (
                {diagnostics?.required_dates.length || 0})
              </Text>
            }
          >
            {renderDateTags(
              diagnostics?.required_dates || [],
              'blue',
              'No required dates for this session.'
            )}
          </Collapse.Panel>
        </Collapse>

        <Input.Search
          allowClear
          placeholder="Search by RIC, name, issuer, or status"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />

        <Tabs
          activeKey={activeTab}
          onChange={key => {
            setActiveTab(key);
            setExpandedRowKeys([]);
          }}
        >
          <Tabs.TabPane key="index_funds" tab="Index Funds" />
          <Tabs.TabPane key="equities" tab="Equities" />
        </Tabs>

        <Table
          rowKey="id"
          loading={waiting}
          columns={columns}
          dataSource={filteredItems}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: waiting ? (
              'Loading diagnostics...'
            ) : (
              <Empty description="No matching options" />
            ),
          }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: keys =>
              setExpandedRowKeys((keys as React.Key[]).map(key => Number(key))),
            expandedRowRender: (record: BenchmarkCoverageDiagnosticItem) => (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {record.missing_count > 0 && renderFillSection(record)}
                <div>
                  <Text strong>Missing dates</Text>
                  <div style={{ marginTop: 8 }}>
                    {renderDateTags(
                      record.missing_dates,
                      'red',
                      'No missing dates. This option is fully covered.'
                    )}
                  </div>
                </div>
                <div>
                  <Text strong>Covered dates</Text>
                  <div style={{ marginTop: 8 }}>
                    {renderDateTags(
                      record.present_dates,
                      'green',
                      'No covered dates found for this option.'
                    )}
                  </div>
                </div>
              </Space>
            ),
          }}
        />
      </Space>
    </Drawer>
  );
};

export default BenchmarkCoverageDiagnosticsDrawer;
